// Branch & Product Analysis (DRAFT) - the page's pure logic, kept out of the component so it can be
// tested without mounting anything.
//
// A pill bar holds one selection (an array of ids) per dimension. "All" is not a value of its own: it
// is lit when every option is selected, and a request for "everything" sends NO parameter for that
// dimension, so the backend adds no predicate and the enquiries with a blank branch or product type are
// counted too (they appear as a "Not set" row or column).

export const NOT_SET = 'none'

// True when every option is selected. An empty option list is never "All".
export function isAll(selected, allIds) {
  return allIds.length > 0 && allIds.every(id => selected.indexOf(id) !== -1)
}

// The "All" pill: everything on, or - when everything already is - everything off.
export function toggleAll(selected, allIds) {
  return isAll(selected, allIds) ? [] : allIds.slice()
}

// One pill on or off. The result keeps the options' own order, so the grid's rows and columns do not
// shuffle with the order the pills were clicked in.
export function togglePill(selected, id, allIds) {
  const on = selected.indexOf(id) === -1
  return allIds.filter(x => (x === id ? on : selected.indexOf(x) !== -1))
}

// The query for one dimension: undefined (no parameter) for All, else the ids.
export function dimensionParam(selected, allIds) {
  return isAll(selected, allIds) ? undefined : selected.slice()
}

// Rows or columns of the grid: the selected options in option order, plus "Not set" when the dimension
// is All and the data holds any blank ones. `hideEmpty` drops those with nothing in them.
export function axis(options, selected, cells, key, hideEmpty) {
  const all = isAll(selected, options.map(o => o.id))
  const used = new Set(cells.map(c => (c[key] === null || c[key] === undefined ? NOT_SET : c[key])))
  const items = options
    .filter(o => selected.indexOf(o.id) !== -1)
    .map(o => ({ id: o.id, label: o.code || o.name, title: o.name }))
  if (all && used.has(NOT_SET)) items.push({ id: NOT_SET, label: 'Not set', title: 'Not set' })
  return hideEmpty ? items.filter(i => used.has(i.id)) : items
}

function keyOf(id) {
  return id === null || id === undefined ? NOT_SET : id
}

// The cell lookup and the totals. Totals are summed from the cells the grid shows, so a total always
// equals the cells beside it.
export function buildGrid(cells, rows, cols) {
  const byKey = {}
  cells.forEach(c => {
    byKey[keyOf(c.branchId) + '|' + keyOf(c.productTypeId)] = c
  })
  const cell = (r, c) => byKey[r + '|' + c] || null
  const add = (t, c) => {
    if (c) {
      t.value += c.value || 0
      t.count += c.count || 0
    }
    return t
  }
  const zero = () => ({ value: 0, count: 0 })
  const rowTotals = {}
  const colTotals = {}
  const grand = zero()
  rows.forEach(r => {
    rowTotals[r.id] = zero()
  })
  cols.forEach(c => {
    colTotals[c.id] = zero()
  })
  rows.forEach(r => {
    cols.forEach(c => {
      const x = cell(r.id, c.id)
      add(rowTotals[r.id], x)
      add(colTotals[c.id], x)
      add(grand, x)
    })
  })
  return { cell, rowTotals, colTotals, grand }
}

// Date pills. The backend resolves them in UK time; "custom" sends from/to as yyyy-MM-dd.
export const PERIODS = [
  { key: 'last3months', label: 'Last 3 months' },
  { key: 'last6months', label: 'Last 6 months' },
  { key: 'last12months', label: 'Last 12 months' },
  { key: 'thisyear', label: 'This year' },
  { key: 'custom', label: 'Custom' }
]

export function isoDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return undefined
  const pad = n => (n < 10 ? '0' + n : '' + n)
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
}

// A debounce that can be cancelled, for the pill clicks.
export function debounce(fn, ms) {
  let timer = null
  const run = function (...args) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn.apply(this, args)
    }, ms)
  }
  run.cancel = () => {
    if (timer) clearTimeout(timer)
    timer = null
  }
  return run
}
